import { useState, useCallback, useEffect, useRef, useContext } from 'react';
import { Box, HStack, Button, Icon } from '@chakra-ui/react';
import usePageTitle from '../../hooks/usePageTitle';
import { TbPlus, TbLayoutDashboard, TbClipboardCheck, TbTable, TbBuilding, TbTruck, TbShield, TbCar, TbChartLine, TbFileText, TbBuildingWarehouse, } from 'react-icons/tb';
import { OverviewTab, AuditingTab, PivotTab, LocationTab, VendorTab, InsuranceTab, OEMTab, ProductivityTab, InvoicingTab, InventoryTab } from './tabs';
import type { AuditingDrilldownPayload } from './widgets/WidgetRenderer';
import ReportingTimeframePopover from './ReportingTimeframePopover';
import { ReportingFiltersProvider } from './ReportingFiltersContext';
import {
    dateToIsoStr,
    getPresetDates,
    loadReportingTimeframe,
    mapPresetToBackend,
    saveReportingTimeframe,
    type DatePreset,
    type Granularity,
    type ReportingTimeframeApplyPayload,
} from './reportingTimeframeUtils';
import { useRecentActivity } from '../../hooks/useRecentActivity';
import ListTabRow from '../../components/ListTabRow';
import SessionContext, { isInventoryOrgSession } from '../../contexts/SessionContext';
import { getInvoicingReportingVisibility } from '../../utils/invoicingToolAccess';
import { hasInventoryReporting } from '../../utils/inventoryReportingAccess';

const ReportingPage = () => {
    usePageTitle('Reporting');

    const getQuerySafePresetRange = useCallback(
        (preset: DatePreset, range: { startDate: string; endDate: string; }) => {
            if (preset !== 'thismonth' && preset !== 'thisquarter') {
                return range;
            }
            const start = new Date(range.startDate + 'T00:00:00');
            const end = new Date(range.endDate + 'T00:00:00');
            if (start.getTime() < end.getTime()) {
                return range;
            }
            const exclusiveEnd = new Date(start);
            exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);
            return {
                startDate: range.startDate,
                endDate: dateToIsoStr(exclusiveEnd),
            };
        },
        []
    );

    const { trackView } = useRecentActivity();
    const addWidgetRef = useRef<(() => void) | null>(null);
    const invoicingAddWidgetRef = useRef<(() => void) | null>(null);
    const inventoryAddWidgetRef = useRef<(() => void) | null>(null);
    const sessionContext = useContext(SessionContext);
    const locationAccess = sessionContext?.locationId;
    // Multi-location GMs can compare their allowed locations; single-location GMs cannot.
    const gmCanCompareLocations =
        locationAccess === 'all'
        || (Array.isArray(locationAccess) && locationAccess.length >= 2);
    /** Suppress location-domain UI for single-location GMs only. */
    const suppressLocationDomain = !gmCanCompareLocations;

    const defaultPreset: DatePreset = '7days';
    const [timeframe] = useState(() => loadReportingTimeframe(defaultPreset));

    const [startDate, setStartDate] = useState(timeframe.startDate);
    const [endDate, setEndDate] = useState(timeframe.endDate);
    const [datePreset, setDatePreset] = useState<DatePreset>(timeframe.preset);
    const [granularity, setGranularity] = useState<Granularity>(timeframe.granularity);
    const [tabIndex, setTabIndex] = useState(() => {
        const saved = localStorage.getItem('reporting_tab_index');
        if (!saved) return 0;
        const parsed = parseInt(saved, 10);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    });
    const [visitedTabs, setVisitedTabs] = useState<Set<number>>(() => new Set([tabIndex]));
    const [pendingAuditingDrilldown, setPendingAuditingDrilldown] = useState<AuditingDrilldownPayload | null>(null);

    const timeFilterRowRef = useRef<HTMLDivElement>(null);

    const handleTabChange = useCallback((index: number) => {
        setTabIndex(index);
        setVisitedTabs((prev) => {
            if (prev.has(index)) return prev;
            const next = new Set(prev);
            next.add(index);
            return next;
        });
    }, []);

    const handleTimeframeApply = useCallback((payload: ReportingTimeframeApplyPayload) => {
        setDatePreset(payload.preset);
        setStartDate(payload.startDate);
        setEndDate(payload.endDate);
        setGranularity(payload.granularity);
    }, []);

    useEffect(() => {
        localStorage.setItem('reporting_tab_index', String(tabIndex));
    }, [tabIndex]);

    useEffect(() => {
        saveReportingTimeframe({ preset: datePreset, startDate, endDate, granularity });
    }, [datePreset, startDate, endDate, granularity]);

    const allTabs: { name: string; icon: React.ElementType; }[] = [
        { name: 'Overview', icon: TbLayoutDashboard as React.ElementType },
        { name: 'Auditing', icon: TbClipboardCheck as React.ElementType },
        { name: 'Pivot', icon: TbTable as React.ElementType },
        { name: 'Location', icon: TbBuilding as React.ElementType },
        { name: 'Vendor', icon: TbTruck as React.ElementType },
        { name: 'Insurance', icon: TbShield as React.ElementType },
        { name: 'OEM', icon: TbCar as React.ElementType },
        { name: 'Productivity', icon: TbChartLine as React.ElementType },
        // { name: 'Ask Yaxle', icon: TbSparkles as React.ElementType },
    ];
    const gmHiddenTabs = new Set([
        'Ask Yaxle',
        'Pivot',
        'Productivity',
        ...(suppressLocationDomain ? ['Location'] : []),
    ]);
    const invoicingVisibility = getInvoicingReportingVisibility(sessionContext);
    const invoicingOnlyReporting = invoicingVisibility === 'invoicing-only';
    const invoicingTabDef = { name: 'Invoicing', icon: TbFileText as React.ElementType };
    const inventoryReportingAvailable = hasInventoryReporting(sessionContext);
    const inventoryTabDef = { name: 'Inventory', icon: TbBuildingWarehouse as React.ElementType };
    const inventoryOnlyReporting = isInventoryOrgSession(sessionContext);
    let tabs: { name: string; icon: React.ElementType; }[];
    if (inventoryOnlyReporting) {
        tabs = inventoryReportingAvailable ? [inventoryTabDef] : [];
    } else if (invoicingOnlyReporting) {
        tabs = [invoicingTabDef];
    } else {
        const base = !sessionContext?.isAdmin ? allTabs.filter((t) => !gmHiddenTabs.has(t.name)) : allTabs;
        tabs = invoicingVisibility === 'reporting-tab' ? [...base, invoicingTabDef] : base;
    }
    if (inventoryReportingAvailable && !inventoryOnlyReporting) {
        tabs = [...tabs, inventoryTabDef];
    }
    const tabNames = tabs.map(t => t.name);
    const maxTabIndex = Math.max(0, tabs.length - 1);
    const safeTabIndex = tabs.length === 0 ? 0 : Math.min(tabIndex, maxTabIndex);
    const invoicingTabIndex = tabs.findIndex((tab) => tab.name === 'Invoicing');
    const inventoryTabIndex = tabs.findIndex((tab) => tab.name === 'Inventory');

    useEffect(() => {
        if (tabIndex !== safeTabIndex) {
            handleTabChange(safeTabIndex);
        }
    }, [handleTabChange, tabIndex, safeTabIndex]);

    // Invoicing-only reporting has no Overview data. Land on Invoicing and mark
    // it visited so widgets fetch without a click (stale localStorage indexes
    // used to leave this tab unmounted).
    useEffect(() => {
        if (!invoicingOnlyReporting || invoicingTabIndex < 0) return;
        if (tabIndex === invoicingTabIndex) return;
        handleTabChange(invoicingTabIndex);
    }, [handleTabChange, invoicingOnlyReporting, invoicingTabIndex, tabIndex]);

    // Inventory-only reporting likewise has no Overview data: land on Inventory.
    useEffect(() => {
        if (!inventoryOnlyReporting || inventoryTabIndex < 0) return;
        if (tabIndex === inventoryTabIndex) return;
        handleTabChange(inventoryTabIndex);
    }, [handleTabChange, inventoryOnlyReporting, inventoryTabIndex, tabIndex]);

    const handleAuditingDrilldown = useCallback((payload: AuditingDrilldownPayload) => {
        const auditingIndex = tabs.findIndex((tab) => tab.name === 'Auditing');
        if (auditingIndex === -1) return;
        setPendingAuditingDrilldown(payload);
        handleTabChange(auditingIndex);
    }, [handleTabChange, tabs]);

    useEffect(() => {
        if (tabIndex >= tabNames.length) {
            handleTabChange(0);
            return;
        }
        const tabName = tabNames[safeTabIndex];
        if (!tabName) return;
        trackView({
            type: 'reporting_tab',
            key: `reporting_tab:${tabName.toLowerCase()}`,
            label: `Reporting • ${tabName}`,
            path: '/reporting',
            meta: { tab: tabName.toLowerCase() },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeTabIndex, trackView, tabNames.length]);

    const backendPreset = mapPresetToBackend(datePreset);
    const activeTabName = tabNames[safeTabIndex];
    const usePageScroll = ['Overview', 'Invoicing', 'Inventory', 'Location', 'Vendor', 'Insurance', 'OEM'].includes(activeTabName);

    // Presets: re-derive from "now" so start/end stay correct after midnight or long sessions.
    // Period-to-date presets end on the latest complete reporting day (08:00 UTC cut), except day-1 of the period.
    // Custom: use the range the user applied (state).
    const displayRange =
        datePreset === 'custom' ? { startDate, endDate } : getPresetDates(datePreset);
    const queryRange =
        datePreset === 'custom' ? displayRange : getQuerySafePresetRange(datePreset, displayRange);
    const reportingStartDate = queryRange.startDate;
    const reportingEndDate = queryRange.endDate;

    return (
        <ReportingFiltersProvider>
            <Box
                w="calc(100% + 2rem)"
                mx={-4}
                mt={-4}
                mb={-2}
                h="calc(100dvh - var(--partbay-topbar))"
                display="flex"
                flexDirection="column"
                pb={0}
                overflowX="hidden"
                overflowY={usePageScroll ? 'auto' : 'hidden'}
            >
                {/* Row 1 — domain tabs + timeframe / add widget, hairline underneath (ENG-2297). */}
                <Box
                    position="sticky"
                    top={0}
                    zIndex={20}
                    bg="white"
                    flexShrink={0}
                >
                    <ListTabRow
                        ref={timeFilterRowRef}
                        actions={
                            <HStack spacing={2} flexShrink={0}>
                                <ReportingTimeframePopover
                                    committedPreset={datePreset}
                                    committedStartDate={displayRange.startDate}
                                    committedEndDate={displayRange.endDate}
                                    onApply={handleTimeframeApply}
                                    boundaryRef={timeFilterRowRef}
                                />
                                {(activeTabName === 'Overview' || activeTabName === 'Invoicing' || activeTabName === 'Inventory') && (
                                    <Button
                                        size="sm"
                                        h="32px"
                                        leftIcon={<Icon as={TbPlus as React.ElementType} boxSize={3.5} />}
                                        bg="#0071E3"
                                        color="white"
                                        _hover={{ bg: '#3544a0' }}
                                        onClick={() => {
                                            if (activeTabName === 'Invoicing') {
                                                invoicingAddWidgetRef.current?.();
                                            } else if (activeTabName === 'Inventory') {
                                                inventoryAddWidgetRef.current?.();
                                            } else {
                                                addWidgetRef.current?.();
                                            }
                                        }}
                                    >
                                        Add Widget
                                    </Button>
                                )}
                            </HStack>
                        }
                    >
                            {tabs.map((tab, i) => {
                                const isActive = safeTabIndex === i;
                                return (
                                    <Button
                                        key={tab.name}
                                        size="sm"
                                        h="32px"
                                        px={4}
                                        borderRadius="full"
                                        fontWeight="medium"
                                        fontSize="sm"
                                        flexShrink={0}
                                        bg={isActive ? '#0071E3' : 'transparent'}
                                        color={isActive ? 'white' : 'gray.600'}
                                        _hover={{
                                            bg: isActive ? '#0071E3' : 'gray.100',
                                        }}
                                        _active={{
                                            bg: isActive ? '#0071E3' : 'gray.100',
                                        }}
                                        leftIcon={<Icon as={tab.icon} boxSize={3.5} />}
                                        onClick={() => handleTabChange(i)}
                                    >
                                        {tab.name}
                                    </Button>
                                );
                            })}
                    </ListTabRow>
                </Box>

                <Box
                    flex={usePageScroll ? 'none' : 1}
                    display="flex"
                    flexDirection="column"
                    overflow={usePageScroll ? 'visible' : 'hidden'}
                    minH={0}
                    px={6}
                    pt={3}
                >

                    <Box
                        flex={usePageScroll ? 'none' : 1}
                        w="100%"
                        minW={0}
                        overflowY={usePageScroll ? 'visible' : 'auto'}
                        overflowX="hidden"
                    >
                        {tabNames.map((name, i) => {
                            if (!visitedTabs.has(i)) return null;
                            const isActive = safeTabIndex === i;
                            const renderContent = () => {
                                switch (name) {
                                    case 'Overview':
                                        return (
                                            <OverviewTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                isGm={suppressLocationDomain}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                                onRegisterAddWidget={(fn) => { addWidgetRef.current = fn; }}
                                                onAuditingDrilldown={handleAuditingDrilldown}
                                            />
                                        );
                                    case 'Auditing':
                                        return (
                                            <AuditingTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                                pendingDrilldown={pendingAuditingDrilldown}
                                                onDrilldownApplied={() => setPendingAuditingDrilldown(null)}
                                            />
                                        );
                                    case 'Pivot':
                                        return (
                                            <PivotTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                                onAuditingDrilldown={handleAuditingDrilldown}
                                            />
                                        );
                                    case 'Location':
                                        return (
                                            <LocationTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                            />
                                        );
                                    case 'Vendor':
                                        return (
                                            <VendorTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                            />
                                        );
                                    case 'Insurance':
                                        return (
                                            <InsuranceTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                            />
                                        );
                                    case 'OEM':
                                        return (
                                            <OEMTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                            />
                                        );
                                    case 'Productivity':
                                        return (
                                            <ProductivityTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                            />
                                        );
                                    case 'Invoicing':
                                        return (
                                            <InvoicingTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                                onRegisterAddWidget={(fn) => { invoicingAddWidgetRef.current = fn; }}
                                            />
                                        );
                                    case 'Inventory':
                                        return (
                                            <InventoryTab
                                                startDate={reportingStartDate}
                                                endDate={reportingEndDate}
                                                granularity={granularity}
                                                preset={backendPreset}
                                                isActive={isActive}
                                                onRegisterAddWidget={(fn) => { inventoryAddWidgetRef.current = fn; }}
                                            />
                                        );
                                    // case 'Ask Yaxle':
                                    //     return (
                                    //         <AskPartBayTab
                                    //             startDate={reportingStartDate}
                                    //             endDate={reportingEndDate}
                                    //         />
                                    //     );
                                    default:
                                        return null;
                                }
                            };
                            return (
                                <Box
                                    key={name}
                                    display={isActive ? 'block' : 'none'}
                                    w="100%"
                                    minW={0}
                                    pb={name === 'Ask Yaxle' || name === 'Auditing' || name === 'Productivity' ? 0 : 4}
                                    h={name === 'Ask Yaxle' || name === 'Auditing' || name === 'Productivity' || name === 'Pivot' ? '100%' : undefined}
                                    overflow={name === 'Ask Yaxle' ? 'hidden' : undefined}
                                >
                                    {renderContent()}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </ReportingFiltersProvider>
    );
};

export default ReportingPage;